/*
 * auth-state.ts -- Single source of truth for MSAL auth state and token acquisition.
 * No DOM dependencies -- safe to use from both taskpane and commands entry points.
 */

/* global Office, localStorage, fetch */

import {
  PublicClientApplication,
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  BrowserCacheLocation,
  PromptValue,
} from "@azure/msal-browser";
import {
  BC_SCOPE,
  createMsalClient,
  getPreferredAccount as pickPreferredAccount,
  formatAuthError,
  clearMsalStorageEntries as clearMsalStorage,
} from "./auth";
import { fetchRuntimeConfigPayload, BcApiEndpointDescriptor } from "./runtime-config";
import { Settings, loadResolvedSettings } from "./settings";
import { extractHostFromHttpUrl, validateBcApiEndpoint } from "./bc-endpoint";

// BC company IDs are GUIDs; validate before URL interpolation
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// --- Grouped state ---

const rtConfig = {
  source: "defaults",
  lastError: "",
  loadedAtMs: 0,
  useTaskpaneConfiguration: true,
  showDiagnosticsSection: true,
  bcApiEndpoint: "",
  bcApiEndpoints: [] as BcApiEndpointDescriptor[],
  azureAppClientId: "",
  azureAppTenantId: "",
  bcApiAllowedHosts: [] as string[],
  allowInsecureBcEndpoint: false,
};

const auth = {
  msalInstance: null as PublicClientApplication | null,
  msalInitPromise: null as Promise<void> | null,
  cachedToken: null as string | null,
  tokenExpiresAtMs: 0,
  currentAccount: null as AccountInfo | null,
  cacheLocation: BrowserCacheLocation.LocalStorage as BrowserCacheLocation,
  lastTokenSource: "none",
  lastAuthError: "",
};

let runtimeConfigLoadPromise: Promise<void> | null = null;

// --- Runtime config listeners ---

export interface RuntimeConfigChangeInfo {
  cacheLocationChanged: boolean;
  clientIdChanged: boolean;
}

export type OnRuntimeConfigReloaded = (changed: RuntimeConfigChangeInfo) => void;
const runtimeConfigListeners: OnRuntimeConfigReloaded[] = [];

export function onRuntimeConfigReloaded(callback: OnRuntimeConfigReloaded): void {
  runtimeConfigListeners.push(callback);
}

// --- State accessors ---

export function getUseTaskpaneConfiguration(): boolean { return rtConfig.useTaskpaneConfiguration; }
export function getShowDiagnosticsSection(): boolean { return rtConfig.showDiagnosticsSection; }
export function getRuntimeAllowInsecureBcEndpoint(): boolean { return rtConfig.allowInsecureBcEndpoint; }
export function getRuntimeBcApiEndpoint(): string { return rtConfig.bcApiEndpoint; }
export function getRuntimeBcApiEndpoints(): BcApiEndpointDescriptor[] {
  if (rtConfig.bcApiEndpoints.length > 0) return rtConfig.bcApiEndpoints;
  if (!rtConfig.bcApiEndpoint) return [];
  return [{ name: extractHostFromHttpUrl(rtConfig.bcApiEndpoint) || "Default", url: rtConfig.bcApiEndpoint }];
}
export function getRuntimeConfigSource(): string { return rtConfig.source; }
export function getRuntimeConfigLastError(): string { return rtConfig.lastError; }
export function getRuntimeConfigLoadedAtMs(): number { return rtConfig.loadedAtMs; }
export function getLastTokenSource(): string { return auth.lastTokenSource; }
export function setLastTokenSource(source: string): void { auth.lastTokenSource = source; }
export function getLastAuthError(): string { return auth.lastAuthError; }
export function setLastAuthError(error: string): void { auth.lastAuthError = error; }
export function getMsalCacheLocation(): BrowserCacheLocation { return auth.cacheLocation; }
export function getCachedToken(): string | null { return auth.cachedToken; }
export function setCachedToken(token: string | null): void { auth.cachedToken = token; }
export function getTokenExpiresAtMs(): number { return auth.tokenExpiresAtMs; }
export function setTokenExpiresAtMs(expiresAt: number): void { auth.tokenExpiresAtMs = expiresAt; }
export function getCurrentAccount(): AccountInfo | null { return auth.currentAccount; }
export function setCurrentAccount(account: AccountInfo | null): void { auth.currentAccount = account; }
export function getMsalInstanceRef(): PublicClientApplication | null { return auth.msalInstance; }

// --- Settings ---

export function loadSettings(storage: Storage = localStorage): Settings {
  return loadResolvedSettings(storage, {
    useTaskpaneConfiguration: rtConfig.useTaskpaneConfiguration,
    runtimeBcApiEndpoint: rtConfig.bcApiEndpoint,
    runtimeAzureAppClientId: rtConfig.azureAppClientId,
  });
}

// --- Runtime config ---

export async function ensureRuntimeConfigLoaded(forceReload = false): Promise<void> {
  if (forceReload) { runtimeConfigLoadPromise = null; }
  if (!runtimeConfigLoadPromise) {
    runtimeConfigLoadPromise = (async () => {
      const prevCacheLocation = auth.cacheLocation;
      const prevEffectiveClientId = loadSettings().clientId.trim();
      const runtimeResult = await fetchRuntimeConfigPayload(fetch);
      if (!("payload" in runtimeResult)) {
        rtConfig.source = runtimeResult.source;
        rtConfig.lastError = runtimeResult.error;
        return;
      }
      const { payload } = runtimeResult;
      rtConfig.useTaskpaneConfiguration = payload.useTaskpaneConfiguration;
      const nextCacheLocation = BrowserCacheLocation.LocalStorage;
      auth.cacheLocation = nextCacheLocation;
      rtConfig.showDiagnosticsSection = payload.showDiagnosticsSection;
      rtConfig.bcApiEndpoint = payload.bcApiEndpoint;
      rtConfig.bcApiEndpoints = payload.bcApiEndpoints;
      rtConfig.azureAppClientId = payload.azureAppClientId;
      rtConfig.azureAppTenantId = payload.azureAppTenantId;
      rtConfig.bcApiAllowedHosts = payload.bcApiAllowedHosts;
      rtConfig.allowInsecureBcEndpoint = payload.allowInsecureBcEndpoint;
      rtConfig.source = payload.runtimeConfigSource;
      rtConfig.lastError = "";
      rtConfig.loadedAtMs = Date.now();
      const nextEffectiveClientId = loadSettings().clientId.trim();
      const cacheLocationChanged = nextCacheLocation !== prevCacheLocation;
      const clientIdChanged = nextEffectiveClientId !== prevEffectiveClientId;
      if ((cacheLocationChanged || clientIdChanged) && auth.msalInstance) {
        resetMsalState();
      }
      for (const listener of runtimeConfigListeners) {
        listener({ cacheLocationChanged, clientIdChanged });
      }
    })();
  }
  await runtimeConfigLoadPromise;
}

// --- MSAL instance management ---

export function getMsalInstance(origin: string = window.location.origin): PublicClientApplication | null {
  const clientId = loadSettings().clientId;
  if (!clientId) return null;
  if (!auth.msalInstance) {
    auth.msalInstance = createMsalClient(clientId, origin, auth.cacheLocation, rtConfig.azureAppTenantId || undefined);
  }
  return auth.msalInstance;
}

export async function ensureMsalInitialized(msal: PublicClientApplication): Promise<void> {
  if (!auth.msalInitPromise) { auth.msalInitPromise = msal.initialize(); }
  await auth.msalInitPromise;
}

export function getLoginHint(): string | undefined {
  return Office.context.mailbox?.userProfile?.emailAddress ?? undefined;
}

export function getAllowedBcApiHosts(): string[] {
  const hosts = [...rtConfig.bcApiAllowedHosts];
  const runtimeConfiguredHost = extractHostFromHttpUrl(rtConfig.bcApiEndpoint);
  if (runtimeConfiguredHost) hosts.push(runtimeConfiguredHost);
  for (const endpoint of rtConfig.bcApiEndpoints) {
    const host = extractHostFromHttpUrl(endpoint.url);
    if (host) hosts.push(host);
  }
  return Array.from(new Set(hosts.map((item) => item.trim().toLowerCase()).filter((item) => item.length > 0)));
}

function getPreferredAccount(msal: PublicClientApplication): AccountInfo | null {
  return pickPreferredAccount(msal, auth.currentAccount, getLoginHint());
}

// --- Token acquisition ---

export async function acquireBcToken(
  interactive: boolean,
  forceAccountPicker = false,
): Promise<AuthenticationResult | null> {
  await ensureRuntimeConfigLoaded();
  const msal = getMsalInstance();
  if (!msal) return null;
  await ensureMsalInitialized(msal);
  const loginHint = getLoginHint();
  // forceAccountPicker skips straight to the interactive popup below even when a cached
  // account/token is already valid -- this is how "Change account" lets the user switch
  // away from the mailbox account without a plain sign-out (which would just silently
  // re-authenticate as the same AAD SSO session on the next sign-in).
  const account = forceAccountPicker ? null : getPreferredAccount(msal);
  if (account) {
    try {
      const result = await msal.acquireTokenSilent({ scopes: [BC_SCOPE], account });
      auth.lastTokenSource = "acquireTokenSilent";
      auth.lastAuthError = "";
      return result;
    } catch (err) {
      if (!(err instanceof InteractionRequiredAuthError)) {
        console.warn("acquireTokenSilent failed:", err);
        auth.lastAuthError = formatAuthError(err);
        // A non-interactive caller (background refresh) should give up quietly here.
        // An explicit interactive sign-in should still fall through to the popup below
        // instead of silently reverting to "Not signed in" without ever showing it --
        // e.g. a stale/leftover account after sign-out can make acquireTokenSilent throw
        // something other than InteractionRequiredAuthError.
        if (!interactive) return null;
      }
    }
  }
  if (!interactive) {
    auth.lastTokenSource = "none";
    return null;
  }
  // select_account forces Azure AD to show its account chooser instead of silently
  // reusing an existing AAD SSO session (which persists in the browser independent of
  // our own signOut()/clearMsalStorageEntries() -- those only clear this app's local
  // MSAL cache). Without it, signing out and immediately signing back in would silently
  // re-authenticate as the same account with no prompt at all.
  const result = await msal.acquireTokenPopup({ scopes: [BC_SCOPE], loginHint, prompt: PromptValue.SELECT_ACCOUNT });
  auth.lastTokenSource = "acquireTokenPopup";
  auth.lastAuthError = "";
  return result;
}

export async function ensureAccessToken(interactive: boolean): Promise<string | null> {
  // 2-minute buffer for in-memory cache reads. Background proactive refresh
  // (TOKEN_REFRESH_LEAD_MS in taskpane-auth.ts) uses a larger 5-minute lead,
  // ensuring silent renewal completes before this threshold is reached.
  const refreshBufferMs = 2 * 60 * 1000;
  if (auth.cachedToken && Date.now() < auth.tokenExpiresAtMs - refreshBufferMs) {
    auth.lastTokenSource = "in-memory cache";
    return auth.cachedToken;
  }
  const result = await acquireBcToken(interactive);
  if (!result) return null;
  applyTokenToState(result);
  return auth.cachedToken;
}

/**
 * Forces a genuinely new access token from AAD via MSAL's own forceRefresh option --
 * unlike just clearing our in-memory cachedToken/tokenExpiresAtMs and calling
 * ensureAccessToken again, which would still hit MSAL's own persisted (localStorage)
 * cache first and, being the same underlying token, almost certainly hand back the exact
 * same JWT. There's no way to tell from a generic BC error alone whether the cached token
 * itself was the problem, but nothing is lost by getting a guaranteed-different one.
 *
 * Silent only -- never prompts. Returns null (without applying anything) if there's no
 * account to refresh silently for, or the silent refresh itself fails; callers should
 * treat that as "carry on with whatever token they already had."
 *
 * Used by the upload retry logic in orders.ts, after users reported an upload failing
 * identically on every manual retry for several minutes yet succeeding immediately after
 * restarting Outlook -- a full restart is the one thing that currently forces AAD to be
 * asked for a token again rather than reusing whatever's cached. Not yet confirmed as the
 * actual cause (see that module's retry logic for the evidence this is meant to gather
 * before it's treated as more than a plausible theory).
 */
export async function forceRefreshAccessToken(): Promise<string | null> {
  const msal = getMsalInstance();
  if (!msal) return null;
  await ensureMsalInitialized(msal);
  const account = getPreferredAccount(msal);
  if (!account) return null;
  try {
    const result = await msal.acquireTokenSilent({ scopes: [BC_SCOPE], account, forceRefresh: true });
    auth.lastTokenSource = "acquireTokenSilent (forceRefresh)";
    auth.lastAuthError = "";
    applyTokenToState(result);
    return auth.cachedToken;
  } catch (err) {
    auth.lastAuthError = formatAuthError(err);
    console.warn("Forced token refresh failed:", err);
    return null;
  }
}

/** Apply token result to core auth state (no UI side effects). */
export function applyTokenToState(result: AuthenticationResult): void {
  auth.cachedToken = result.accessToken;
  auth.tokenExpiresAtMs = result.expiresOn ? result.expiresOn.getTime() : Date.now() + 45 * 60 * 1000;
  auth.currentAccount = result.account ?? null;
  const msal = getMsalInstance();
  if (msal && auth.currentAccount) { msal.setActiveAccount(auth.currentAccount); }
}

// --- BC connection context ---

export async function getBcConnectionContext(interactive: boolean): Promise<{ token: string; bcUrl: string }> {
  const token = await ensureAccessToken(interactive);
  if (!token) throw new Error("Not signed in.");
  const { bcUrl } = loadSettings();
  if (!bcUrl) throw new Error("BC API endpoint is not configured.");
  const validation = validateBcApiEndpoint(bcUrl, {
    allowedHosts: getAllowedBcApiHosts(),
    allowInsecureHttp: rtConfig.allowInsecureBcEndpoint,
  });
  if (!validation.ok) throw new Error(validation.error);
  return { token, bcUrl: validation.normalizedUrl };
}

export interface BcRequestContext {
  token: string;
  bcUrl: string;
  companyId: string;
  departmentSetEntryIdFlt: string;
}

export async function getBcRequestContext(interactive: boolean): Promise<BcRequestContext> {
  const connection = await getBcConnectionContext(interactive);
  const { companyId, departmentSetEntryIdFlt } = loadSettings();
  if (!companyId) throw new Error("Company is not selected.");
  if (!GUID_RE.test(companyId)) throw new Error("Company ID format is invalid.");
  return { token: connection.token, bcUrl: connection.bcUrl, companyId, departmentSetEntryIdFlt };
}

// --- Auth state reset ---

export function resetMsalState(): void {
  auth.msalInstance = null;
  auth.msalInitPromise = null;
  auth.cachedToken = null;
  auth.tokenExpiresAtMs = 0;
  auth.currentAccount = null;
  auth.lastTokenSource = "none";
}

export function clearMsalStorageEntries(clientId: string, storages: Storage[] = [localStorage]): void {
  clearMsalStorage(clientId, storages);
}

// --- Error formatting ---

export { formatAuthError };
